import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Send, Search, CheckCircle2, AlertTriangle, Clock, FileText, Loader2, XCircle, Mail } from 'lucide-react';
import API from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import SdiBadge from '../components/SdiBadge';

// Mappatura stati SDI (ei_status di FattureInCloud) — usata solo per i filtri
const EI_STATI = {
  not_sent:    { label: 'Non inviata',  cls: 'bg-slate-100 text-slate-700', icon: Mail },
  pending:     { label: 'In coda',      cls: 'bg-sky-100 text-sky-700', icon: Clock },
  processing:  { label: 'In transito',  cls: 'bg-amber-100 text-amber-700', icon: Clock },
  accepted:    { label: 'Consegnata',   cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected:    { label: 'Scartata',     cls: 'bg-rose-100 text-rose-700', icon: XCircle },
  error:       { label: 'Errore SDI',   cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
  not_received:{ label: 'Mancata cons.',cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

const fmtEUR = (n) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('it-IT'); } catch { return s; } };

export default function FattureAttivePage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [eiStatus, setEiStatus] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year, page, per_page: perPage });
      if (q) params.set('q', q);
      if (eiStatus) params.set('ei_status', eiStatus);
      const { data } = await API.get(`/api/sdi/fatture-attive?${params}`);
      setItems(data.items || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
    } catch (e) {
      alert('Errore caricamento: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [year, q, eiStatus, page]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`/api/sdi/fatture-attive/sync?year=${year}`);
      let msg = `Sync completato: ${data.inserted} nuove, ${data.updated} aggiornate`;
      if (data.errors?.length) msg += `\nWarning: ${data.errors.join('; ')}`;
      alert(msg);
      await load();
    } catch (e) {
      alert('Errore sync: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleInviaSDI = async (fattura) => {
    const ok = await confirm({
      title: 'Inviare al SDI?',
      message: `Vuoi inviare la fattura n. ${fattura.numero_completo || fattura.numero} di ${fmtEUR(fattura.importo_totale)} al Sistema di Interscambio? L'invio è definitivo.`,
      confirmLabel: 'Invia ora',
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      await API.post(`/api/sdi/fatture-attive/${fattura.id}/invia-sdi`);
      alert('Fattura inviata al SDI con successo. Lo stato si aggiorna a "In transito".');
      await load();
      if (selected?.id === fattura.id) {
        const { data } = await API.get(`/api/sdi/fatture-attive/${fattura.id}`);
        setSelected(data);
      }
    } catch (e) {
      alert('Errore invio SDI: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshStatus = async (fattura) => {
    setActionLoading(true);
    try {
      const { data } = await API.post(`/api/sdi/fatture-attive/${fattura.id}/refresh`);
      setItems(items.map(i => i.id === fattura.id ? data : i));
      if (selected?.id === fattura.id) setSelected(data);
    } catch (e) {
      alert('Errore refresh: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setActionLoading(false);
    }
  };

  const StatoBadge = ({ s, message }) => (
    <SdiBadge ei={s} message={message} size="sm" />
  );

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-5" data-testid="fatture-attive-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fatture Attive</h1>
          <p className="text-sm text-slate-500">Fatture emesse + invio Sistema di Interscambio (SDI) tramite FattureInCloud</p>
        </div>
        <button onClick={handleSync} disabled={syncing} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-bold hover:bg-primary-dark disabled:opacity-60" data-testid="sync-attive-btn">
          {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          {syncing ? 'Sincronizzazione...' : `Sincronizza ${year}`}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {['not_sent', 'processing', 'accepted', 'rejected', 'error'].map(k => {
          const cfg = EI_STATI[k]; const s = stats[k] || { count: 0, tot: 0 }; const Icon = cfg.icon;
          return (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs"><Icon size={14} /> {cfg.label}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{s.count}</div>
              <div className="text-xs text-slate-500">{fmtEUR(s.tot)}</div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input type="text" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Cerca cliente, P.IVA, numero..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>
        <select value={year} onChange={e => { setYear(parseInt(e.target.value)); setPage(1); }} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          {[0, 1, 2, 3].map(o => { const y = new Date().getFullYear() - o; return <option key={y} value={y}>{y}</option>; })}
        </select>
        <select value={eiStatus} onChange={e => { setEiStatus(e.target.value); setPage(1); }} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
          <option value="">Tutti gli stati SDI</option>
          {Object.entries(EI_STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        {loading ? <div className="p-8 text-center text-slate-500">Caricamento...</div>
        : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <FileText size={32} className="mx-auto mb-2 opacity-30" />
            <div>Nessuna fattura attiva per {year}.</div>
            <div className="text-xs mt-1">Clicca <b>Sincronizza {year}</b> per scaricarle da FattureInCloud.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Numero</th>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-right">Imponibile</th>
                <th className="px-3 py-2 text-right">Totale</th>
                <th className="px-3 py-2 text-center">Stato SDI</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(f.data)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{f.numero_completo || f.numero || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{f.cliente_nome || '—'}</div>
                    {f.cliente_piva && <div className="text-xs text-slate-500">P.IVA {f.cliente_piva}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(f.importo_netto)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtEUR(f.importo_totale)}</td>
                  <td className="px-3 py-2 text-center"><StatoBadge s={f.ei_status} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setSelected(f)} className="text-primary hover:underline text-xs font-semibold mr-2">Dettagli</button>
                    {(f.ei_status === 'not_sent' || f.ei_status === 'error' || f.ei_status === 'rejected') && (
                      <button onClick={() => handleInviaSDI(f)} className="inline-flex items-center gap-1 px-2 py-1 bg-primary text-white rounded text-xs font-semibold hover:bg-primary-dark" data-testid={`invia-sdi-${f.id}`}>
                        <Send size={12} /> Invia SDI
                      </button>
                    )}
                    {(f.ei_status === 'processing' || f.ei_status === 'pending') && (
                      <button onClick={() => handleRefreshStatus(f)} disabled={actionLoading} className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50">
                        <RefreshCw size={12} /> Aggiorna
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div>Totale: {total} fatture</div>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50">Prev</button>
            <span className="px-3 py-1">Pagina {page} di {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3">
          <div className="bg-white rounded-xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <div className="font-bold text-lg">Fattura {selected.numero_completo || selected.numero || '—'}</div>
                <div className="text-sm text-slate-500">{selected.cliente_nome} · {fmtDate(selected.data)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 text-2xl">×</button>
            </div>
            <div className="p-4 overflow-auto space-y-4">
              <div className="flex items-center gap-3">
                <StatoBadge s={selected.ei_status} />
                {selected.ei_message && <span className="text-xs text-rose-600">{selected.ei_message}</span>}
                <button onClick={() => handleRefreshStatus(selected)} className="ml-auto text-xs text-primary inline-flex items-center gap-1 hover:underline">
                  <RefreshCw size={12} /> Aggiorna stato
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">P.IVA</div><div>{selected.cliente_piva || '—'}</div></div>
                <div><div className="text-xs text-slate-500">CF</div><div>{selected.cliente_cf || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Imponibile</div><div className="font-semibold">{fmtEUR(selected.importo_netto)}</div></div>
                <div><div className="text-xs text-slate-500">IVA</div><div>{fmtEUR(selected.importo_iva)}</div></div>
                <div className="col-span-2"><div className="text-xs text-slate-500">Totale</div><div className="font-bold text-lg">{fmtEUR(selected.importo_totale)}</div></div>
                {selected.descrizione && <div className="col-span-2"><div className="text-xs text-slate-500">Oggetto</div><div>{selected.descrizione}</div></div>}
              </div>
              {selected.ei_data && Object.keys(selected.ei_data).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-500">Dati tecnici SDI (ei_data)</summary>
                  <pre className="mt-2 p-2 bg-slate-50 rounded overflow-auto text-[10px]">{JSON.stringify(selected.ei_data, null, 2)}</pre>
                </details>
              )}
            </div>
            <div className="p-4 border-t flex justify-between gap-2 sticky bottom-0 bg-white">
              {(selected.ei_status === 'not_sent' || selected.ei_status === 'error' || selected.ei_status === 'rejected') ? (
                <button onClick={() => handleInviaSDI(selected)} disabled={actionLoading} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded font-bold text-sm hover:bg-primary-dark disabled:opacity-60">
                  <Send size={14} /> {actionLoading ? 'Invio in corso...' : 'Invia al SDI'}
                </button>
              ) : <span />}
              <button onClick={() => setSelected(null)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded text-sm font-semibold">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
